import { FaSolidMinus, FaSolidPlus } from "solid-icons/fa"
import { Component, createSignal, onCleanup, Show, useContext } from "solid-js"
import type { Engine } from "../../../lib/core/engine/Engine.type"
import { ELEVATION_ABSOLUTE_MAX, ELEVATION_ABSOLUTE_MIN, ELEVATION_STEP } from "../../../lib/core/entity/EntityElevation/elevation_consts"
import isEntityHasFloorElevation from "../../../lib/core/entity/EntityElevation/isEntityHasFloorElevation"
import isEntityHasWaterElevation from "../../../lib/core/entity/EntityElevation/isEntityHasWaterElevation"
import findByPosition from "../../../lib/core/findByPosition"
import { createSetEntityElevationAction, type SetEntityElevationAction } from "../../../lib/core/state/flux/action/Map/SetEntityElevationAction"
import dispatchClient from "../../../lib/core/state/flux/dispatchClient"
import type { TilePositionXY } from "../../../lib/core/tile_position_xy/TilePositionXY.type"
import Button from "../../../lib/jsx/Button"
import isEmptyArray from "../../../lib/ldsh/isEmptyArray"
import { useSelectedPositionListSignal } from "../../../rx/signal/useSelectedPositionListSignal"
import { useSelectedPositionSignal } from "../../../rx/signal/useSelectedPositionSignal"
import useSelectionModeSignal from "../../../rx/signal/useSelectionModeSignal"
import { useTileNodeListSignal } from "../../../rx/signal/useTileNodeListSignal"
import AlertWarning from "../../AlertWarning"
import SelectSelectionMode from "../../Btn/SelectSelectionMode"
import EngineContext from "../../EngineContext"
import FormGroup from "../../Form/FormGroup"
import FormLabel from "../../Form/FormLabel"
import createElevationTool from "./createElevationTool"

const ElevationTool: Component = () => {
  const engine: Engine = useContext(EngineContext)
  const [elevationDelta, setElevationDelta] = createSignal(1)
  const [targetElevation, setTargetElevation] = createSignal(0)

  const [getSelectedPositionList] = useSelectedPositionListSignal(engine)
  const [getSelectedPosition] = useSelectedPositionSignal(engine)
  const [getTileNodeList] = useTileNodeListSignal(engine)
  const [getSelectionMode, setSelectionMode] = useSelectionModeSignal(engine)

  const elevationTool = createElevationTool(engine)
  onCleanup(elevationTool.dispose)

  function makeFn (property: 'water_e' | 'floor_e') {
    return (delta: number) => {
      const spList = getSelectedPositionList()
      const { ents } = engine.state
  
      const data: SetEntityElevationAction['data'] = []
      for (let i = 0; i < ents.length; i++) {
        const ent = ents[i]
        if ((property in ent) && findByPosition(spList, ent)) {
          const oldValue = (ent as Record<typeof property, number>)[property]
          data.push({
            ent_id: ent.id,
            [property]: oldValue + delta,
          })
        }
      }
      // console.log('data', data.map(v => v.elevation))
      dispatchClient(engine, createSetEntityElevationAction(data))
    }
  }

  const updateFloorElevation = makeFn('floor_e')
  const updateWaterLevel = makeFn('water_e')

  return (
    <div class="elevation-tool card-body">

      <div class="gap-2">
        <FormGroup>
          <FormLabel>Selection Mode</FormLabel>
          <SelectSelectionMode 
            value={getSelectionMode()} 
            onChange={setSelectionMode} 
          />
        </FormGroup>
      </div>

      <div class="gap-2">
        <FormGroup>
          <FormLabel>Set Floor Elevation</FormLabel>
          <div class="input-group mb-2">
            <input
              type="number"
              class="form-control"
              value={targetElevation()}
              step={ELEVATION_STEP}
              min={ELEVATION_ABSOLUTE_MIN}
              max={ELEVATION_ABSOLUTE_MAX}
              onChange={(e) => setTargetElevation(parseFloat(e.target.value) || 0)}
              placeholder="Enter elevation"
            />
            <Button
              class="btn btn-outline-secondary"
              disabled={isEmptyArray(getSelectedPositionList())}
              onClick={() => {
                const spList = getSelectedPositionList()
                const { ents } = engine.state
                
                const data: SetEntityElevationAction['data'] = []
                for (let i = 0; i < ents.length; i++) {
                  const ent = ents[i]
                  if (isEntityHasFloorElevation(ent) && findByPosition(spList, ent)) {
                    data.push({
                      ent_id: ent.id,
                      floor_e: targetElevation(),
                    })
                  }
                }
                dispatchClient(engine, createSetEntityElevationAction(data))
              }}
              aria-label="Set Floor Elevation"
            >
              Set
            </Button>
          </div>
        </FormGroup>
      </div>
      
      <FormGroup>
        <FormLabel>Elevation Adjustment</FormLabel>
        <div class="d-flex gap-2 justify-content-center">
          <Button
            onClick={() => updateFloorElevation(-elevationDelta())} 
            disabled={elevationDelta() <= 0}
            class="btn btn-outline-primary w-25"
          >
            <FaSolidMinus /> Lower
          </Button>

          <input
            type="number"
            class="form-control text-center w-25"
            value={elevationDelta()}
            step={ELEVATION_STEP}
            min={ELEVATION_ABSOLUTE_MIN}
            max={ELEVATION_ABSOLUTE_MAX}
            onChange={(e) => setElevationDelta(parseFloat(e.target.value) || 0)}
          />

          <Button
            onClick={() => updateFloorElevation(elevationDelta())} 
            disabled={elevationDelta() <= 0}
            class="btn btn-outline-primary w-25"
          >
            <FaSolidPlus /> Raise
          </Button>
        </div>
      </FormGroup>

      <div class="gap-2">
        <FormGroup>
          <FormLabel>Set Water Level</FormLabel>
          <div class="input-group mb-2">
            <input
              type="number"
              class="form-control"
              value={targetElevation()}
              step={ELEVATION_STEP}
              min={ELEVATION_ABSOLUTE_MIN}
              max={ELEVATION_ABSOLUTE_MAX}
              onChange={(e) => setTargetElevation(parseFloat(e.target.value) || 0)}
              placeholder="Enter Water Level"
            />
            <Button
              class="btn btn-outline-secondary"
              disabled={isEmptyArray(getSelectedPositionList())}
              onClick={() => {
                const spList = getSelectedPositionList()
                const { ents } = engine.state
                
                const data: SetEntityElevationAction['data'] = []
                for (let i = 0; i < ents.length; i++) {
                  const ent = ents[i]
                  if (isEntityHasFloorElevation(ent) && findByPosition(spList, ent)) {
                    data.push({
                      ent_id: ent.id,
                      water_e: targetElevation(),
                    })
                  }
                }
                dispatchClient(engine, createSetEntityElevationAction(data))
              }}
              aria-label="Set Water Level"
            >
              Set
            </Button>
          </div>
        </FormGroup>
      </div>

      <FormGroup>
        <FormLabel>Water Level Adjustment</FormLabel>
        <div class="d-flex gap-2 justify-content-center">
          <Button
            onClick={() => updateWaterLevel(-elevationDelta())} 
            disabled={elevationDelta() <= 0}
            class="btn btn-outline-primary w-25"
          >
            <FaSolidMinus /> Lower
          </Button>

          <input
            type="number"
            class="form-control text-center w-25"
            value={elevationDelta()}
            step={ELEVATION_STEP}
            min={ELEVATION_ABSOLUTE_MIN}
            max={ELEVATION_ABSOLUTE_MAX}
            onChange={(e) => setElevationDelta(parseFloat(e.target.value) || 0)}
          />

          <Button
            onClick={() => updateWaterLevel(elevationDelta())} 
            disabled={elevationDelta() <= 0}
            class="btn btn-outline-primary w-25"
          >
            <FaSolidPlus /> Raise
          </Button>
        </div>
      </FormGroup>

      <div class="mt-3">
        <small class="text-muted">
          Click on tiles to select them, then adjust elevation using the controls above.
        </small>
      </div>

      <Show when={getSelectedPosition()} keyed children={(selectedPosition: TilePositionXY) => {
        const tileNode = findByPosition(getTileNodeList(), selectedPosition)
        if (tileNode) {
          return <div class="mt-3 text-muted justify-content-center">
            <small>
              Selected Position: <span class="text-monospace">{selectedPosition.x}, {selectedPosition.y}</span>
            </small>&nbsp;
            <Show when={tileNode.ents.l1()} keyed children={(l1Entity) => {
              return (
                <>
                  {('elevation' in l1Entity) && (<div>
                    Legacy Elevation: <span class="text-monospace">{l1Entity.elevation}</span>
                  </div>)}
                  {isEntityHasFloorElevation(l1Entity) && (<div>
                    Elevation: <span class="text-monospace">{l1Entity.floor_e}</span>
                  </div>)}
                  {isEntityHasWaterElevation(l1Entity) && (<div>
                    Water Level: <span class="text-monospace">{l1Entity.water_e}</span>
                  </div>)}
                </>
              )
            }} />
          </div>
        }
        return <AlertWarning>
          Invalid Selection: <span class="text-monospace">{selectedPosition.x}, {selectedPosition.y}</span>
        </AlertWarning>
      }}/>
    </div>
  )
}

export default ElevationTool
